import { useMemo } from 'react';
import styled from 'styled-components';
import {
  Breadcrumbs as BaseBreadcrumbs,
  BreadcrumbsProps,
  Typography,
  Divider,
} from '@mui/material';
import { ReactComponent as TreeIcon } from '../../assets/tree.svg';
import { ReactComponent as TreeCollapsedIcon } from '../../assets/tree-collapsed.svg';
import { ReactComponent as TreeCollapsedFilteredIcon } from '../../assets/tree-collapsed-filtered.svg';
import { ReactComponent as ArrowIcon } from '../../assets/16x16/arrow-left.svg';
import { Flex } from '../Flex';
import InternalLink from '../InternalLink';
import IconButton from '../IconButton';
import useToggleTreeView from '../../hooks/useToggleTreeView';
import useTreeViewParams from '../../hooks/useTreeViewParams';
import { fontSize, fontWeight } from '../../style/utils';

type BreadcrumbsItem =
  | string
  | {
      label: string;
      onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
    };

type Props = BreadcrumbsProps & {
  items: BreadcrumbsItem[];
  showTreeButton?: boolean;
  className?: string;
};

const StyledLink = styled(InternalLink)`
  color: ${({ theme }) => theme.palette.content.disabled};
  font-size: ${fontSize('lg')};
  text-decoration: none;

  :hover {
    text-decoration: underline;
  }
`;

const Breadcrumbs = ({
  items,
  showTreeButton = true,
  className,
  ...props
}: Props) => {
  const { treeViewCollapsed, toggleTreeView } = useToggleTreeView();
  const { urlTreeNode, urlSavedView } = useTreeViewParams();

  const lastItem = items[items.length - 1];

  const collapsedIcon = useMemo(
    () =>
      urlTreeNode || urlSavedView ? (
        <TreeCollapsedFilteredIcon />
      ) : (
        <TreeCollapsedIcon />
      ),
    [urlTreeNode, urlSavedView],
  );

  const treeIcon = useMemo(
    () => (treeViewCollapsed.isCollapsed ? collapsedIcon : <TreeIcon />),
    [treeViewCollapsed.isCollapsed, collapsedIcon],
  );

  return (
    <Flex
      sx={{ minHeight: '34px', alignItems: 'center', gap: 1 }}
      className={className}
    >
      {showTreeButton && (
        <>
          <IconButton sx={{ p: '1px !important' }} onClick={toggleTreeView}>
            {treeIcon}
          </IconButton>

          <Divider sx={{ height: '27px', mr: 1 }} orientation="vertical" />
        </>
      )}

      <BaseBreadcrumbs separator={<ArrowIcon />} {...props}>
        {items.slice(0, -1).map((item) =>
          typeof item === 'string' ? (
            <Typography
              key={item}
              sx={{ fontSize: fontSize('lg'), color: 'content.disabled' }}
            >
              {item}
            </Typography>
          ) : (
            <StyledLink
              key={item.label}
              to=""
              onClick={(e) => item.onClick?.(e)}
            >
              {item.label}
            </StyledLink>
          ),
        )}

        <Typography
          sx={{ fontSize: fontSize('lg'), fontWeight: fontWeight('medium') }}
        >
          {typeof lastItem === 'string' ? lastItem : lastItem.label}
        </Typography>
      </BaseBreadcrumbs>
    </Flex>
  );
};

export default Breadcrumbs;
